import { call, put, select } from 'typed-redux-saga';

import { setIsOneOnOne } from '../../../shared/features/config';
import { itemRatioHash } from '../../../shared/features/settings/sagas';
import { getAvatarPlaceholder } from '../../../shared/helpers/avatar-placeholder';
import { createLogger } from '../../../shared/helpers/loggerFacade';
import { VIRTUAL_CALLEE } from '../constants';
import { setChatUnreadCount } from '../features/chat';
import { cancelCallAction } from '../features/conference/slice';
import { toggleMuteAction } from '../features/device/slice';
import { setIsFullscreenAction } from '../features/fullscreen/';
import { setStoreGridItemRatio } from '../features/goloom-video-grid/slice';
import { shareScreenAction } from '../features/screen-sharing/slice';
import { selectParticipants } from '../selectors';
import { ParticipantState } from '../types';
import {
    acceptCallAction,
    screenRecordingAction,
    setOutgoingCallCallee,
    setOutgoingCallStatus,
    syncMediaDeviceAction,
} from './slice';
import { IncomingEvents, IncomingPostMessage } from './types';

const logger = createLogger('handleIframeMessage');

function* setOutgoingCallCalleeSaga(participantState: Maybe<ParticipantState>) {
    const participants = yield* select(selectParticipants);
    if (participants.length >= 2) {
        return;
    }
    yield* put(setOutgoingCallCallee(participantState));
}

/**
 * @param {Object} message
 */
export function* handleMessage(message: IncomingPostMessage) {
    switch (message.event) {
        case IncomingEvents.SET_MUTE: {
            yield* put(toggleMuteAction(message.payload));
            break;
        }

        case IncomingEvents.SET_GRID_ITEM_RATIO: {
            const gridItemRatio = itemRatioHash[message.payload.value];

            if (gridItemRatio) {
                yield* put(setStoreGridItemRatio({ gridItemRatio }));
            }
            break;
        }

        case IncomingEvents.CANCEL_CALL: {
            yield* put(cancelCallAction());
            break;
        }

        case IncomingEvents.SHARE_SCREEN: {
            yield* put(shareScreenAction(message.payload.value));
            break;
        }

        case IncomingEvents.UNREAD_COUNT: {
            yield* put(setChatUnreadCount(message.payload.value));
            break;
        }

        case IncomingEvents.SET_MEDIA_DEVICE: {
            yield* put(syncMediaDeviceAction(message.payload));

            break;
        }

        case IncomingEvents.SET_CALLEE_PARTICIPANT: {
            yield* call(setOutgoingCallCalleeSaga, {
                id: VIRTUAL_CALLEE,
                displayName: message.payload.displayName,
                avatarUrl: message.payload.avatarUrl,
                avatarPlaceholder: getAvatarPlaceholder({ name: message.payload.displayName }),
            });

            break;
        }

        case IncomingEvents.SET_CALL_STATUS: {
            yield* put(setOutgoingCallStatus(message.payload));

            break;
        }

        case IncomingEvents.SET_IS_ONE_ON_ONE: {
            yield* put(setIsOneOnOne(message.payload.value));

            break;
        }

        case IncomingEvents.CLOSE_FULL_SCREEN: {
            yield* put(setIsFullscreenAction(false));

            break;
        }

        case IncomingEvents.ACCEPT_CALL: {
            yield* put(acceptCallAction());

            break;
        }

        case IncomingEvents.START_SCREEN_RECORDING_RESULT:
        case IncomingEvents.ADD_SCREEN_RECORDING_CHUNK_RESULT:
        case IncomingEvents.END_SCREEN_RECORDING_RESULT: {
            yield* put(screenRecordingAction(message));

            break;
        }

        default:
            logger.warn('Unhandled iframe message event', {
                method: 'handleMessage',
                additional: {
                    message: message.event,
                },
            });
            break;
    }
}
